import type { Action, Getter, MutationTree } from 'vuex';
import type { ActivityItem } from '@@/packages/shared/types/models/activity';
import type { State as RootState } from '@/store/useStore';
import api from './api';
import { groupProfileLevelActivityItems } from './activityItemGrouping';

export type ActivityItemsState = {
  // API items only; group items are synthesised by the getter and never stored.
  byID: Record<string, ActivityItem>;
  byConnID: Record<string, { byAssociatedID: Record<string, string[]>; allIDs: string[] }>;
};

const initialState = (): ActivityItemsState => ({
  byID: {},
  byConnID: {
    // Expected structure
    // 'conn-id-1': {
    //   byAssociatedID: {
    //     'control-1': ['activity-id-1', 'activity-id-2'],
    //     'control-2': ['activity-id-3'],
    //   },
    //   allIDs: ['activity-id-1', 'activity-id-2', 'activity-id-4'],
    // },
  },
});

const fetchConnectionActivityItems: Action<ActivityItemsState, RootState> = async (
  { commit },
  { connectionID, limit, fromDate, toDate, excluding },
) => {
  const res = await api.fetchConnectionActivityItems(
    connectionID,
    limit,
    fromDate,
    toDate,
    excluding,
  );
  commit('ADD_MANY', res.data.activityItems);

  // Append ids
  const activityIDs = res.data.activityItems.map((item) => item.id);
  commit('ADD_BY_CONNID_ALLIDS', { connectionID, activityIDs });

  return res;
};

const actions = {
  fetchConnectionActivityItems,
};

const mutations: MutationTree<ActivityItemsState> = {
  FLUSH(state) {
    Object.assign(state, initialState());
  },
  ADD_MANY(state, items: ActivityItem[]) {
    state.byID = items.reduce<Record<string, ActivityItem>>(
      (obj, item) => {
        obj[item.id] = item;
        return obj;
      },
      { ...state.byID },
    );
  },
  ADD_BY_CONNID_ALLIDS(
    state,
    { connectionID, activityIDs }: { connectionID: string; activityIDs: string[] },
  ) {
    const existing = state.byConnID[connectionID];
    if (!existing) {
      state.byConnID[connectionID] = { byAssociatedID: {}, allIDs: activityIDs };
    } else {
      // Newest first; a refetch can bring back items newer than those held.
      existing.allIDs = [...new Set([...existing.allIDs, ...activityIDs])].sort(
        (a, b) => Date.parse(state.byID[b]?.date ?? '') - Date.parse(state.byID[a]?.date ?? ''),
      );
    }
  },
};

const filterActivityItemsBeforeInitialAssessmentComplete = (
  activityItems: ActivityItem[],
  targetOrgType: 'supplier' | 'client',
  targetOrgInitAssComplete: boolean,
) => {
  const initialAssessmentCompleteActivityIdx = activityItems.findIndex(
    (item) => item.type === 'initial_assessment_completed',
  );
  const notAllowedBeforeInitialAssessmentComplete: readonly string[] = [
    'answer_changed',
    'notes_changed',
    'evidence_added',
    'evidence_removed',
    'compliance_changed',
    'connection_compliance_changed',
    'scoping_answer_changed',
  ];

  // CASE: Initial Assessment Complete activity is not inside current array of items
  if (initialAssessmentCompleteActivityIdx < 0) {
    // If client viewing supplier activity, filter items before initial assessment completed
    if (targetOrgType === 'supplier' && targetOrgInitAssComplete === false) {
      return activityItems.filter(
        (item) => !notAllowedBeforeInitialAssessmentComplete.includes(item.type),
      );
    }

    return activityItems;
  }

  // CASE: Initial Assessment Complete activity is inside current array of items
  // If client viewing supplier activity, filter items before initial assessment complete
  if (targetOrgType === 'supplier') {
    const afterInitialAssessmentComplete = activityItems.slice(
      0,
      initialAssessmentCompleteActivityIdx + 1,
    );

    const beforeInitialAssessmentComplete = activityItems
      .slice(initialAssessmentCompleteActivityIdx + 1)
      .filter((item) => !notAllowedBeforeInitialAssessmentComplete.includes(item.type));

    return [...afterInitialAssessmentComplete, ...beforeInitialAssessmentComplete];
  }

  return activityItems;
};

const filterNonRequiredControlActivityItems = (
  requiredDomainIDs: string[],
  requiredControlIDs: string[],
  activityItems: ActivityItem[],
) => {
  const controlRelatedActivityItems: readonly string[] = [
    'answer_changed',
    'notes_changed',
    'evidence_added',
    'evidence_removed',
  ];
  const domainRelatedActivityItems: readonly string[] = ['scoping_answer_changed'];

  if (!requiredDomainIDs || !requiredControlIDs) {
    return activityItems;
  }

  return activityItems?.filter((item) => {
    if (controlRelatedActivityItems.includes(item.type)) {
      return item.associatedID !== undefined && requiredControlIDs.includes(item.associatedID);
    }

    if (domainRelatedActivityItems.includes(item.type)) {
      return item.associatedID !== undefined && requiredDomainIDs.includes(item.associatedID);
    }

    return true;
  });
};

const getConnectionActivityItems: Getter<ActivityItemsState, RootState> =
  (state, _getters, rootState, _rootGetters) =>
  (
    connectionID: string,
    targetOrgType: 'supplier' | 'client',
    targetOrgInitAssComplete: boolean,
  ) => {
    const connection = state.byConnID[connectionID];
    if (connection && Array.isArray(connection.allIDs)) {
      // Remove activity items before initial assessment complete
      // Find initial assessment complete item index
      let activityItems = connection.allIDs
        .map((id) => state.byID[id])
        .filter((item): item is ActivityItem => item !== undefined);

      activityItems = filterActivityItemsBeforeInitialAssessmentComplete(
        activityItems,
        targetOrgType,
        targetOrgInitAssComplete,
      );

      // Remove activity items related to non required controls based on framework levels
      const requiredDomainIDs = rootState?.framework?.frameworkRequiredDomainIDs;
      const requiredControlIDs = rootState?.framework?.frameworkRequiredControlIDs;

      activityItems = filterNonRequiredControlActivityItems(
        requiredDomainIDs,
        requiredControlIDs,
        activityItems,
      );

      return groupProfileLevelActivityItems(activityItems);
    }
    return null;
  };

const getters = {
  getConnectionActivityItems,
};

export default {
  namespaced: true,
  state: initialState(),
  actions,
  mutations,
  getters,
};
